import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, FileSignature, Ticket, PenSquare, CheckSquare,
  Globe, RefreshCw, ShieldCheck,
  Plus, Search, ChevronDown, Receipt, Building2, Briefcase, MailPlus,
  TrendingUp, DollarSign,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { fmtCents } from '../../lib/financial';
import { nyToday } from '../../lib/datetime';
import Tile, { CardShell } from '../../components/admin/dashboard/Tile';
import NewAccountDialog from '../../components/admin/AccountForm';
import NewInvoiceDialog from '../../components/admin/NewInvoiceDialog';
import TaskFormDialog from '../../components/admin/TaskFormDialog';

// /admin/dashboard — admin command-center landing.
//
// Tier 1: alert tiles (overdue invoices, unsigned, tickets ≥48h, edits ≥48h)
// Tier 2: today's work (my tasks, stage-aging, outreach replies placeholder)
// Tier 3: money (admin-only) — AR, MTD booked/collected, top open balances,
//          AR aging strip
// Tier 4: pipeline funnel + delivery capacity
// Tier 5: recent activity + risks (domains, renewals, low-score audits)
//
// Each block fetches its own endpoint; failure of one doesn't break the rest.

const HOURS_48_MS = 48 * 60 * 60 * 1000;

// Coerces an ISO/SQL date string to a Date or null.
function asDate(s) {
  if (!s) return null;
  const iso = String(s).replace(' ', 'T');
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return Number.isNaN(d.valueOf()) ? null : d;
}

function fmtDelta(curr, prev) {
  if (!prev || prev === 0) return null;
  const pct = ((curr - prev) / prev) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs prev`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'nexsav_admin';
  const [creators, setCreators] = useState({ account: false, invoice: false, task: false });

  // Each block holds its own state so a single 500 doesn't blank the page.
  const [financial, setFinancial] = useState({ loading: true, data: null });
  const [aging, setAging] = useState({ loading: true, data: null });
  const [tickets, setTickets] = useState({ loading: true, data: null });
  const [edits, setEdits] = useState({ loading: true, data: null });
  const [tasks, setTasks] = useState({ loading: true, data: null });
  const [upcoming, setUpcoming] = useState({ loading: true, tasks: [], meetings: [] });
  const [clientHealth, setClientHealth] = useState({ loading: true, data: null });
  const [pipeline, setPipeline] = useState({ loading: true, data: null });
  const [capacity, setCapacity] = useState({ loading: true, data: null });
  const [activity, setActivity] = useState({ loading: true, data: null });
  const [domains, setDomains] = useState({ loading: true, data: null });
  const [renewals, setRenewals] = useState({ loading: true, data: null });
  const [auditAnalytics, setAuditAnalytics] = useState({ loading: true, data: null });
  const [needsReply, setNeedsReply] = useState({ loading: true, data: null });

  const loadAll = useCallback(async () => {
    // Parallel fetch with per-block error swallow. Each setter just records
    // null on failure so the empty-state path renders instead of a stack trace.
    const safe = async (path) => {
      try { return await api.get(path); } catch { return null; }
    };

    const [
      finRes, agingRes, ticketsRes, editsRes, tasksRes, healthRes,
      pipelineRes, capacityRes, activityRes, domainsRes, renewalsRes, auditRes,
      needsReplyRes,
    ] = await Promise.all([
      isAdmin ? safe('/api/admin/financial/dashboard') : Promise.resolve(null),
      isAdmin ? safe('/api/admin/financial/aging') : Promise.resolve(null),
      safe('/api/admin/tickets?status=open&limit=100'),
      safe('/api/admin/edit-requests?status=open'),
      safe('/api/admin/tasks/mine?include_done=false'),
      safe('/api/admin/analytics/client-health'),
      safe('/api/admin/analytics/pipeline'),
      safe('/api/admin/projects/capacity'),
      safe('/api/admin/activity?limit=10'),
      safe('/api/admin/domains?expiring=true&window_days=30'),
      isAdmin ? safe('/api/admin/financial/maintenance-renewals?window_days=60') : Promise.resolve(null),
      safe('/api/admin/analytics/audits'),
      safe('/api/admin/communications/needs-reply?count=1'),
    ]);
    // Coming up: my tasks due in the next 7 days, and meetings on the pipeline board.
    const [upcomingRes, meetingsRes] = await Promise.all([
      safe('/api/admin/tasks/mine?view=upcoming'),
      safe('/api/admin/accounts?lifecycle_stage=engaged&meeting_scheduled=1&limit=50'),
    ]);
    setUpcoming({ loading: false, tasks: upcomingRes?.tasks || [], meetings: (meetingsRes?.accounts || []).filter((a) => a.meetingScheduledAt).sort((a, b) => a.meetingScheduledAt.localeCompare(b.meetingScheduledAt)) });

    setFinancial({ loading: false, data: finRes });
    setAging({ loading: false, data: agingRes });
    setTickets({ loading: false, data: ticketsRes });
    setEdits({ loading: false, data: editsRes });
    setTasks({ loading: false, data: tasksRes });
    setClientHealth({ loading: false, data: healthRes });
    setPipeline({ loading: false, data: pipelineRes });
    setCapacity({ loading: false, data: capacityRes });
    setActivity({ loading: false, data: activityRes });
    setDomains({ loading: false, data: domainsRes });
    setRenewals({ loading: false, data: renewalsRes });
    setAuditAnalytics({ loading: false, data: auditRes });
    setNeedsReply({ loading: false, data: needsReplyRes });
  }, [isAdmin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ───────── derived counts (Tier 1 + 2) ─────────
  const overdueData = useMemo(() => {
    if (!financial.data) return { count: 0, totalCents: 0 };
    return {
      count: financial.data.overdueCount || 0,
      totalCents: financial.data.ar?.outstandingCents || 0,
    };
  }, [financial.data]);

  const unsignedCount = useMemo(() => {
    // Aging buckets only count active receivables; the dashboard exposes a
    // sigantureRequiredUnsigned summary directly when present, otherwise
    // estimate as 0 and rely on the dedicated drill page.
    return financial.data?.signatureRequiredUnsigned ?? 0;
  }, [financial.data]);

  const ticketsAging = useMemo(() => {
    if (!tickets.data?.tickets) return { agedCount: 0, totalCount: 0 };
    const cutoff = Date.now() - HOURS_48_MS;
    const aged = tickets.data.tickets.filter((t) => {
      const created = asDate(t.created_at || t.createdAt);
      return created && created.getTime() < cutoff;
    });
    return { agedCount: aged.length, totalCount: tickets.data.tickets.length };
  }, [tickets.data]);

  const editsAging = useMemo(() => {
    const list = edits.data?.editRequests || edits.data?.edit_requests || [];
    if (list.length === 0) return { agedCount: 0, totalCount: 0 };
    const cutoff = Date.now() - HOURS_48_MS;
    const aged = list.filter((e) => {
      const created = asDate(e.createdAt || e.created_at);
      return created && created.getTime() < cutoff;
    });
    return { agedCount: aged.length, totalCount: list.length };
  }, [edits.data]);

  const myTasks = useMemo(() => {
    const list = tasks.data?.tasks || [];
    const today = nyToday(); // F14 — NY-local "today" so evening tasks bucket correctly
    return list
      .filter((t) => t.due_date || t.dueDate)
      .filter((t) => {
        const d = (t.due_date || t.dueDate || '').slice(0, 10);
        return d <= today;
      })
      .slice(0, 5);
  }, [tasks.data]);

  const stuckAccounts = useMemo(() => {
    // Composite-health < 0.4 is the "needs reach-out" bucket.
    const list = clientHealth.data?.passes || [];
    return list.filter((p) => p.compositeHealth < 0.4).slice(0, 5);
  }, [clientHealth.data]);

  // ───────── Tier 4 derived ─────────
  const funnelData = pipeline.data?.funnel || [];
  const capacityData = capacity.data || null;

  // ───────── Tier 5 derived ─────────
  const domainsExpiring = (domains.data?.domains || []).length;
  const renewalsDue = (renewals.data?.renewals || renewals.data?.items || []).length;
  const lowAudits = useMemo(() => {
    const dist = auditAnalytics.data?.scoreDistribution || auditAnalytics.data?.score_distribution || null;
    if (!dist) return 0;
    return (dist['<70'] || 0) + (dist.lt70 || 0);
  }, [auditAnalytics.data]);

  const recentActivity = activity.data?.activity || [];

  // ───────── Phase 3: Quick actions + search ─────────
  const [search, setSearch] = useState('');
  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/admin/accounts?q=${encodeURIComponent(q)}`);
  };

  // ───────── render ─────────
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);
  const today = useMemo(() => new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }), []);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto space-y-6">
      {/* Header — greeting + quick actions + search */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            {greeting}, <span className="serif-acc accent-text">{user?.name?.split(' ')[0] || 'there'}</span>
          </h1>
          <p className="text-[13px] text-gray-500 mt-0.5">{today}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={onSearchSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            <input
              data-testid="dashboard-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search accounts…"
              className="pl-9 pr-3 py-2 rounded-full bg-white/[0.03] border border-white/[0.08] text-[13px] text-white placeholder:text-gray-500 w-[260px] focus:outline-none focus:border-[#B0FC38]/40"
            />
          </form>
          <NewMenu
            onPick={(kind) => setCreators((s) => ({ ...s, [kind]: true }))}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      {/* Tier 1 — Needs my attention now */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-tier1">
        <Tile
          label="Overdue invoices"
          value={overdueData.count}
          subtitle={overdueData.count > 0 ? fmtCents(overdueData.totalCents) + ' outstanding' : 'Clean, no overdue'}
          tone={overdueData.count > 0 ? 'alert' : 'ok'}
          to="/admin/invoices?overdue=true"
          icon={AlertCircle}
          loading={financial.loading}
          testid="tile-overdue-invoices"
        />
        <Tile
          label="Unsigned invoices"
          value={unsignedCount}
          subtitle={unsignedCount > 0 ? 'Awaiting client signature' : 'All signed'}
          tone={unsignedCount > 0 ? 'warn' : 'ok'}
          to="/admin/invoices?status=sent"
          icon={FileSignature}
          loading={financial.loading}
          testid="tile-unsigned-invoices"
        />
        <Tile
          label="Tickets ≥48h"
          value={ticketsAging.agedCount}
          subtitle={ticketsAging.agedCount > 0
            ? `${ticketsAging.totalCount} open total`
            : ticketsAging.totalCount > 0 ? `${ticketsAging.totalCount} open, all <48h` : 'Inbox zero'}
          tone={ticketsAging.agedCount > 0 ? 'warn' : 'ok'}
          to="/admin/tickets?status=open"
          icon={Ticket}
          loading={tickets.loading}
          testid="tile-tickets-aging"
        />
        <Tile
          label="Edit requests ≥48h"
          value={editsAging.agedCount}
          subtitle={editsAging.agedCount > 0
            ? `${editsAging.totalCount} open total`
            : editsAging.totalCount > 0 ? `${editsAging.totalCount} open, all <48h` : 'Caught up'}
          tone={editsAging.agedCount > 0 ? 'warn' : 'ok'}
          to="/admin/edit-requests?status=open"
          icon={PenSquare}
          loading={edits.loading}
          testid="tile-edits-aging"
        />
      </div>

      {/* Tier 2 — Today's work */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-tier2">
        <CardShell
          title="My tasks"
          testid="card-my-tasks"
          action={<Link to="/admin/tasks?scope=mine" className="text-[11px] text-gray-400 hover:text-white">View all →</Link>}
        >
          {tasks.loading ? <SkeletonRows n={3} /> : myTasks.length === 0 ? (
            <EmptyText>No tasks due today or overdue. Nice.</EmptyText>
          ) : (
            <ul className="space-y-1.5 text-[12.5px]">
              {myTasks.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-white/[0.04] bg-white/[0.015]">
                  <span className="text-gray-200 truncate">{t.title}</span>
                  <span className="text-[10px] text-gray-500 font-mono shrink-0">{(t.due_date || t.dueDate || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
          {/* Coming up: reminders for the week ahead (follow-up tasks, next actions, meetings). */}
          {!upcoming.loading && (upcoming.tasks.length > 0 || upcoming.meetings.length > 0) && (
            <div className="mt-3 pt-2.5 border-t border-white/[0.05]" data-testid="card-coming-up">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Coming up · next 7 days</div>
              <ul className="space-y-1 text-[12px]">
                {upcoming.meetings.slice(0, 4).map((a) => (
                  <li key={`m-${a.id}`} className="flex items-center justify-between gap-2 px-2 py-1 rounded border border-violet-500/15 bg-violet-500/[0.04]">
                    <Link to={`/admin/accounts/${a.id}`} className="text-violet-200 truncate hover:text-white">Meeting · {a.name}</Link>
                    <span className="text-[10px] text-gray-500 font-mono shrink-0">{a.meetingScheduledAt}</span>
                  </li>
                ))}
                {upcoming.tasks.slice(0, 5).map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded border border-white/[0.04]">
                    <span className="text-gray-300 truncate">{t.title}</span>
                    <span className="text-[10px] text-gray-500 font-mono shrink-0">{(t.dueDate || t.due_date || '').slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/admin/tasks?scope=mine&view=upcoming" className="inline-block mt-1.5 text-[11px] text-gray-400 hover:text-white">All upcoming →</Link>
            </div>
          )}
        </CardShell>

        <CardShell
          title="Stage-aging stuck"
          testid="card-stage-aging"
          action={<Link to="/admin/accounts" className="text-[11px] text-gray-400 hover:text-white">All accounts →</Link>}
        >
          {clientHealth.loading ? <SkeletonRows n={3} /> : stuckAccounts.length === 0 ? (
            <EmptyText>All maintained accounts trending healthy.</EmptyText>
          ) : (
            <ul className="space-y-1.5 text-[12.5px]">
              {stuckAccounts.map((p) => (
                <li key={p.accountId}>
                  <Link
                    to={`/admin/accounts/${p.accountId}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-amber-500/15 bg-amber-500/[0.03] hover:bg-amber-500/[0.07]"
                  >
                    <span className="text-gray-200 truncate">{p.accountName}</span>
                    <span className="text-[10px] text-amber-300 shrink-0">
                      {p.daysSinceLastContact != null ? `${p.daysSinceLastContact}d` : '—'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardShell>

        <CardShell
          title="Needs reply"
          testid="card-outreach-awaiting"
          action={<Link to="/admin/communications?tab=needs-reply" className="text-[11px] text-gray-400 hover:text-white">Open queue →</Link>}
        >
          {needsReply.loading ? <SkeletonRows n={2} /> : (() => {
            const total = Number(needsReply.data?.total) || 0;
            const mine = Number(needsReply.data?.mine) || 0;
            if (needsReply.data == null) {
              return <EmptyText>Couldn’t load the reply queue.</EmptyText>;
            }
            if (total === 0) {
              return <EmptyText>Every school that wrote has heard back.</EmptyText>;
            }
            return (
              <Link to="/admin/communications?tab=needs-reply" className="block group" data-testid="card-outreach-awaiting-link">
                <div className="text-[28px] font-semibold leading-none text-amber-200 tabular-nums" data-testid="card-outreach-awaiting-count">{total}</div>
                <div className="text-[12px] text-gray-400 mt-1.5">
                  {total === 1 ? 'school is' : 'schools are'} waiting on us · <span className="text-gray-200">{mine} assigned to you</span>
                </div>
              </Link>
            );
          })()}
        </CardShell>
      </div>

      {/* Tier 3 — Money (admin only) */}
      {isAdmin ? (
        <MoneySection
          financial={financial}
          aging={aging}
        />
      ) : (
        <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 text-[12px] text-gray-500">
          Financial overview is admin-only. Ask Ammar for an export if you need it.
        </div>
      )}

      {/* Tier 4 — Pipeline + delivery */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dashboard-tier4">
        <CardShell
          title="Pipeline funnel"
          testid="card-pipeline-funnel"
          action={<Link to="/admin/pipeline" className="text-[11px] text-gray-400 hover:text-white">Open Pipeline →</Link>}
        >
          {pipeline.loading ? <SkeletonRows n={4} /> : (
            <ul className="space-y-2">
              {funnelData.map((f) => {
                const max = Math.max(...funnelData.map((x) => x.count), 1);
                const pct = (f.count / max) * 100;
                return (
                  <li key={f.stage}>
                    <Link
                      to={`/admin/accounts?lifecycle_stage=${f.stage}&stage_view=all`}
                      className="block hover:opacity-90"
                    >
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-gray-300 capitalize">{f.stage.replace(/_/g, ' ')}</span>
                        <span className="text-white font-mono">{f.count}</span>
                      </div>
                      <div className="h-1.5 rounded bg-white/[0.04] overflow-hidden">
                        <div
                          className="h-full rounded bg-gradient-to-r from-[#B0FC38]/60 to-[#B0FC38]/60"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardShell>

        <CardShell
          title="Delivery capacity"
          testid="card-capacity"
          action={<Link to="/admin/projects" className="text-[11px] text-gray-400 hover:text-white">Projects →</Link>}
        >
          {capacity.loading ? <SkeletonRows n={3} /> : capacityData == null ? (
            <EmptyText>No active projects.</EmptyText>
          ) : (
            <div className="grid grid-cols-3 gap-2 text-center">
              <CapacityCell label="In build" value={capacityData.inBuildCount ?? capacityData.in_build ?? 0} />
              <CapacityCell label="In delivery" value={capacityData.inDeliveryCount ?? capacityData.in_delivery ?? 0} />
              <CapacityCell label="Live" value={capacityData.liveCount ?? capacityData.live ?? 0} />
            </div>
          )}
        </CardShell>
      </div>

      {/* Tier 5 — Recent activity + risks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-tier5">
        <CardShell
          title="Recent activity"
          className="md:col-span-2"
          testid="card-activity"
          action={<Link to="/admin/activity" className="text-[11px] text-gray-400 hover:text-white">Full feed →</Link>}
        >
          {activity.loading ? <SkeletonRows n={6} /> : recentActivity.length === 0 ? (
            <EmptyText>No recent admin activity.</EmptyText>
          ) : (
            <ul className="divide-y divide-white/[0.04]">
              {recentActivity.slice(0, 8).map((row) => (
                <li key={row.id} className="py-2 flex items-center justify-between gap-2 text-[12px]">
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-300 truncate">
                      <span className="text-white font-medium">{row.actionType?.replace(/\./g, ' › ').replace(/_/g, ' ') || '—'}</span>
                      {row.entityType && row.entityId && (
                        <span className="text-gray-500"> · {row.entityType} {String(row.entityId).slice(0, 8)}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-600 font-mono">{(row.createdAt || row.created_at || '').slice(0, 19).replace('T', ' ')}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardShell>

        <CardShell title="Risks" testid="card-risks">
          <div className="space-y-2 text-[12.5px]">
            <RiskRow
              icon={Globe}
              label="Domains expiring ≤30d"
              count={domainsExpiring}
              to="/admin/domains?expiring=true"
              loading={domains.loading}
            />
            {isAdmin && (
              <RiskRow
                icon={RefreshCw}
                label="Renewals due ≤60d"
                count={renewalsDue}
                to="/admin/financial"
                loading={renewals.loading}
              />
            )}
            <RiskRow
              icon={ShieldCheck}
              label="Audits scoring <70"
              count={lowAudits}
              to="/admin/audits"
              loading={auditAnalytics.loading}
            />
          </div>
        </CardShell>
      </div>

      {/* Quick-action dialogs */}
      {creators.account && (
        <NewAccountDialog
          open
          onClose={() => setCreators((s) => ({ ...s, account: false }))}
          onSaved={(acc) => { setCreators((s) => ({ ...s, account: false })); if (acc?.id) navigate(`/admin/accounts/${acc.id}`); }}
        />
      )}
      {creators.invoice && (
        <NewInvoiceDialog
          open
          onClose={() => setCreators((s) => ({ ...s, invoice: false }))}
          onCreated={(inv) => { setCreators((s) => ({ ...s, invoice: false })); if (inv?.id) navigate(`/admin/invoices/${inv.id}`); }}
        />
      )}
      {creators.task && (
        <TaskFormDialog
          open
          onClose={() => setCreators((s) => ({ ...s, task: false }))}
          onSaved={() => { setCreators((s) => ({ ...s, task: false })); loadAll(); }}
        />
      )}
    </div>
  );
}

// ───────── small subcomponents ─────────

function NewMenu({ onPick, isAdmin }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="dashboard-new-menu"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[12.5px] font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/10"
      >
        <Plus className="w-3.5 h-3.5" /> New <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-xl border border-white/[0.08] bg-[#0a0a0f] p-1.5">
            <NewItem icon={Building2} label="Account" onClick={() => { setOpen(false); onPick('account'); }} />
            {isAdmin && (
              <NewItem icon={Receipt} label="Invoice" onClick={() => { setOpen(false); onPick('invoice'); }} />
            )}
            <NewItem icon={CheckSquare} label="Task" onClick={() => { setOpen(false); onPick('task'); }} />
            <NewItem icon={MailPlus} label="Outreach campaign" to="/admin/outreach" closeOnClick={() => setOpen(false)} />
            <NewItem icon={Briefcase} label="Project" to="/admin/projects" closeOnClick={() => setOpen(false)} />
          </div>
        </>
      )}
    </div>
  );
}

function NewItem({ icon: Icon, label, onClick, to, closeOnClick }) {
  const cls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12.5px] text-gray-300 hover:bg-white/[0.04] hover:text-white w-full text-left';
  if (to) {
    return (
      <Link to={to} className={cls} onClick={closeOnClick}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function MoneySection({ financial, aging }) {
  const f = financial.data;
  const a = aging.data;
  const mtdBooked = f?.revenue?.bookedThisMonthCents ?? 0;
  const pmBooked = f?.revenue?.bookedPrevMonthCents ?? 0;
  const mtdCollected = f?.revenue?.collectedThisMonthCents ?? 0;
  const pmCollected = f?.revenue?.collectedPrevMonthCents ?? 0;
  const top = f?.topOutstandingAccounts || [];
  return (
    <div data-testid="dashboard-tier3" className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label="AR outstanding"
          value={fmtCents(f?.ar?.outstandingCents || 0)}
          subtitle={f?.ar?.billedCents ? `${fmtCents(f.ar.paidCents || 0)} of ${fmtCents(f.ar.billedCents)} collected` : null}
          tone="accent"
          to="/admin/financial"
          icon={DollarSign}
          loading={financial.loading}
          testid="tile-ar-outstanding"
        />
        <Tile
          label="Booked this month"
          value={fmtCents(mtdBooked)}
          subtitle={fmtDelta(mtdBooked, pmBooked)}
          tone="neutral"
          to="/admin/financial"
          icon={TrendingUp}
          loading={financial.loading}
          testid="tile-booked-mtd"
        />
        <Tile
          label="Collected this month"
          value={fmtCents(mtdCollected)}
          subtitle={fmtDelta(mtdCollected, pmCollected)}
          tone="ok"
          to="/admin/financial"
          loading={financial.loading}
          testid="tile-collected-mtd"
        />
        <CardShell title="Top open balances" testid="card-top-open">
          {financial.loading ? <SkeletonRows n={2} /> : top.length === 0 ? (
            <EmptyText>No outstanding balances.</EmptyText>
          ) : (
            <ul className="space-y-1.5 text-[12px]">
              {top.slice(0, 3).map((t) => (
                <li key={t.accountId}>
                  <Link to={`/admin/accounts/${t.accountId}?tab=financial`} className="flex justify-between gap-2 hover:bg-white/[0.04] px-1.5 py-1 rounded">
                    <span className="text-gray-200 truncate">{t.accountName}</span>
                    <span className="text-white font-mono shrink-0">{fmtCents(t.outstandingCents)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardShell>
      </div>

      {/* AR aging strip */}
      {a?.buckets && (
        <div data-testid="dashboard-aging-strip" className="grid grid-cols-4 gap-2">
          {['0-30', '31-60', '61-90', '90+'].map((b) => (
            <Link
              key={b}
              to={`/admin/invoices?aging=${encodeURIComponent(b)}`}
              data-testid={`aging-bucket-${b}`}
              className="rounded-xl border border-white/[0.05] bg-white/[0.015] p-3 hover:bg-white/[0.025] transition-colors"
            >
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Aging {b}</div>
              <div className="text-[15px] text-white font-semibold mt-0.5">{fmtCents(a.buckets[b]?.total_cents || 0)}</div>
              <div className="text-[10px] text-gray-500">{a.buckets[b]?.count || 0} invoice{(a.buckets[b]?.count || 0) === 1 ? '' : 's'}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CapacityCell({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.05] bg-white/[0.015] p-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-[20px] text-white font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function RiskRow({ icon: Icon, label, count, to, loading, testid }) {
  const tone = count > 0 ? 'text-amber-300' : 'text-gray-500';
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03]"
    >
      <span className="flex items-center gap-2 text-gray-300 truncate">
        <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        {label}
      </span>
      {loading ? (
        <span className="w-6 h-4 rounded bg-white/[0.06] animate-pulse" />
      ) : (
        <span className={`font-mono font-semibold ${tone}`}>{count}</span>
      )}
    </Link>
  );
}


function SkeletonRows({ n = 3 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-7 rounded bg-white/[0.03] animate-pulse" />
      ))}
    </div>
  );
}

function EmptyText({ children }) {
  return <div className="text-[12px] text-gray-500 italic">{children}</div>;
}
